import { CheckCircle } from '@phosphor-icons/react';
import { ButtonDock } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Cell, List, Section, Text } from '@telegram-apps/telegram-ui';
import { useState, type FC } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FocusLanguage } from '../client/generated/graphql';
import { updateUserMutation } from '../client/mutations/updateUser';
import { Page } from '../components/Page';
import { changeLanguage } from '../i18n';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../i18n/resolveLanguage';

const FOCUS_LANGUAGE_ENUM_BY_CODE: Record<SupportedLanguage, FocusLanguage> = {
    en: FocusLanguage.En,
    ru: FocusLanguage.Ru,
    cs: FocusLanguage.Cs,
};

export const LanguagePage: FC = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation('common');
    const [updateUser] = useMutation(updateUserMutation);
    const [saving, setSaving] = useState(false);
    const currentLanguage = i18n.language as SupportedLanguage;

    const selectLanguage = async (language: SupportedLanguage) => {
        if (language === currentLanguage || saving) return;
        setSaving(true);
        try {
            await updateUser({ variables: { data: { languageCode: FOCUS_LANGUAGE_ENUM_BY_CODE[language] } } });
            await changeLanguage(language);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Page
            back={false}
            footer={<ButtonDock secondary={{ text: t('actions.back'), onClick: () => navigate(-1), navigation: 'back' }} />}
        >
            <Box sx={{ px: 2, pt: 2 }}>
                <Stack spacing={0.5} sx={{ pb: 2 }}>
                    <Text style={{ fontSize: 20, fontWeight: 700 }}>{t('profile.settings.title')}</Text>
                    <Text style={{ color: 'var(--tg-theme-hint-color)', fontSize: 13 }}>{t('profile.settings.subtitle')}</Text>
                </Stack>
                <Text
                    Component="div"
                    style={{
                        fontSize: 13,
                        fontWeight: 600,
                        lineHeight: 1.25,
                        color: 'var(--tg-theme-hint-color)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em',
                        padding: '4px 2px 8px',
                    }}
                >
                    {t('profile.language.title')}
                </Text>
                <List style={{ padding: 0 }}>
                    <Section>
                        {SUPPORTED_LANGUAGES.map(language => (
                            <Cell
                                key={language}
                                onClick={() => void selectLanguage(language)}
                                disabled={saving}
                                after={
                                    language === currentLanguage ? (
                                        <CheckCircle size={22} color="var(--tgui--button_color)" weight="fill" />
                                    ) : undefined
                                }
                            >
                                {t(`profile.language.options.${language}`)}
                            </Cell>
                        ))}
                    </Section>
                </List>
            </Box>
        </Page>
    );
};
